import { useRef, useState } from "react";

type ConfirmationState<Subject> =
  | { status: "closed" }
  | { status: "confirming"; subject: Subject }
  | { status: "running"; subject: Subject };

/**
 * Owns a confirmation dialog: what it asks about, and the write that follows
 * the Confirm. A write in flight owns the dialog: Cancel waits for it, and a
 * second Confirm is ignored. A failed write reopens the question, so the
 * caller reports the failure and the reader can try again. A write that
 * throws closes the dialog and rejects: a write reports its expected failure
 * as "failed", so a throw is a defect and must not leave the dialog running.
 *
 * The ref closes the same-render gap between two clicks, as in
 * `useSingleFlightAction`; the state drives the dialog.
 */
function useConfirmation<Subject>(): {
  state: ConfirmationState<Subject>;
  ask: (subject: Subject) => void;
  cancel: () => void;
  confirm: (
    write: (subject: Subject) => Promise<"done" | "failed">
  ) => Promise<void>;
} {
  const [state, setState] = useState<ConfirmationState<Subject>>({
    status: "closed",
  });
  const stateRef = useRef<ConfirmationState<Subject>>({ status: "closed" });

  function set(next: ConfirmationState<Subject>): void {
    stateRef.current = next;
    setState(next);
  }

  async function confirm(
    write: (subject: Subject) => Promise<"done" | "failed">
  ): Promise<void> {
    const current = stateRef.current;
    if (current.status !== "confirming") return;
    const { subject } = current;
    set({ status: "running", subject });
    let outcome: "done" | "failed";
    try {
      outcome = await write(subject);
    } catch (error) {
      set({ status: "closed" });
      throw error;
    }
    switch (outcome) {
      case "done":
        set({ status: "closed" });
        return;
      case "failed":
        set({ status: "confirming", subject });
        return;
      default:
        throw new Error(outcome satisfies never);
    }
  }

  return {
    state,
    ask: (subject) => {
      if (stateRef.current.status === "running") return;
      set({ status: "confirming", subject });
    },
    cancel: () => {
      if (stateRef.current.status === "running") return;
      set({ status: "closed" });
    },
    confirm,
  };
}

export { useConfirmation };
